import { ACCESSORIES, MARKINGS, PERSONALITIES, SKINS, characterStyle, type CharacterStyle } from '../core/characters';
import { PLAYER_COLORS, PLAYER_COLORS_CB, PLAYER_COLOR_NAMES } from '../core/players';
import { characterPreview, type CharacterPreview, type PreviewPose } from './characterPreview';
import { choiceControl } from './controls';
import type { UserSettings } from './settingsStore';

const title = (s: string) => s[0]!.toUpperCase() + s.slice(1);
const PERSONALITY_HINTS = {
  confident: 'A relaxed stance, steady stride, and a victory salute.',
  nervous: 'Tucked arms, quick steps, and a shy little wave.',
  scrappy: 'Raised fists, a driving stride, and a fighting spirit.',
  sleepy: 'Heavy eyelids, loose arms, and an easygoing shuffle.',
};
const OPTION_HINTS: Record<string, string> = {
  confident: 'Cool & collected', nervous: 'A little jumpy', scrappy: 'Ready to rumble', sleepy: 'Taking it easy',
  none: 'Keep it simple', headband: 'Tied for trouble', glasses: 'A sharper look', wraps: 'Ready knuckles', gloves: 'Big mitts', scarf: 'A little flourish',
  plain: 'Clean color', stripe: 'One bold line', chevron: 'Double trouble', spots: 'A little character',
  classic: 'Your original colors', circuit: 'Angular light trails', pulse: 'A bright chest ring',
};
const CATEGORIES = [
  { field: 'personality', label: 'Personality', description: 'Set your stance, expression, and signature moves.', choices: PERSONALITIES },
  { field: 'accessory', label: 'Accessories', description: 'One finishing touch. Pick the one that feels like you.', choices: ACCESSORIES },
  { field: 'marking', label: 'Markings', description: 'Give your fighter a little extra character.', choices: MARKINGS },
  { field: 'skin', label: 'Skins', description: 'A new finish in your fighter’s color.', choices: SKINS },
] as const;
type AppearanceField = typeof CATEGORIES[number]['field'];
type Category = AppearanceField | 'preview';

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag); el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

export function characterRoom(card: HTMLElement, colorIndex: number, settings: UserSettings, onChange: (style: CharacterStyle) => void, onBack: () => void): void {
  card.classList.add('character-room');
  const header = element('header', 'character-header');
  const identity = element('div', 'character-identity');
  identity.append(element('p', 'character-eyebrow', 'Dressing room'), element('h2', '', `${PLAYER_COLOR_NAMES[colorIndex]} fighter`));
  header.append(identity, element('p', 'character-header-hint', 'Your look. Your signature moves.'));

  let style = characterStyle(settings.characters[colorIndex], colorIndex);
  let weapon = 'none', pose: PreviewPose = 'idle', facing = 1, aim = 0.08, category: Category = 'personality';
  const reducedMotion = settings.reduceShake || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let playing = !reducedMotion;
  const color = (settings.colorblind ? PLAYER_COLORS_CB : PLAYER_COLORS)[colorIndex]!;
  card.style.setProperty('--fighter-color', color);
  const layout = element('div', 'character-room-layout');
  const stage = element('section', 'character-stage'); stage.setAttribute('aria-label', 'Fighter preview');
  const stageTop = element('div', 'character-stage-top');
  const stageTag = element('span', 'character-stage-tag', 'Your fighter');
  const playback = element('button', 'character-playback', playing ? 'Pause preview' : 'Play preview');
  playback.type = 'button'; playback.id = 'character-playback'; playback.hidden = reducedMotion;
  playback.addEventListener('click', () => { playing = !playing; refresh(); });
  stageTop.append(stageTag, playback);
  const preview = element('div', 'character-preview-host');
  const figure = characterPreview(style, color, { animated: true, reducedMotion, weapon, pose });
  preview.append(figure);
  const stageInfo = element('div', 'character-stage-info');
  const personality = element('h3', 'character-personality-name');
  const description = element('p', 'character-description');
  const equipped = element('p', 'character-equipped');
  stageInfo.append(personality, description, equipped);
  stage.append(stageTop, preview, stageInfo);

  const wardrobe = element('section', 'character-wardrobe'); wardrobe.setAttribute('aria-label', 'Customize fighter');
  const panels = element('div', 'character-panels');
  const panelMap = new Map<Category, HTMLElement>();
  const thumbnails = new Map<AppearanceField, { value: string; svg: CharacterPreview }[]>();
  const selectionNotes = new Map<AppearanceField, HTMLElement>();
  const categories = choiceControl({ id: 'character-category', label: 'Customize', value: category,
    choices: [...CATEGORIES.map(({ field, label }) => ({ value: field, label })), { value: 'preview', label: 'Try it out' }],
    onChange: (value) => { category = value as Category; refresh(); panels.scrollTop = 0; },
  });
  categories.classList.add('character-categories');

  for (const row of CATEGORIES) {
    const panel = element('section', 'character-panel'); panel.id = `character-panel-${row.field}`;
    const heading = element('h3', 'character-panel-title', row.label); heading.id = `${panel.id}-heading`;
    panel.setAttribute('aria-labelledby', heading.id);
    panel.append(heading, element('p', 'character-panel-hint', row.description));
    const field = choiceControl({ id: `character-${row.field}`, label: row.label, value: style[row.field] ?? 'classic',
      choices: row.choices.map((value) => ({ value, label: title(value) })),
      onChange: (value) => { style = characterStyle({ ...style, [row.field]: value }, colorIndex); onChange(style); refresh(); },
    });
    // Inventory tiles use the existing spatial cursor: move to a tile, then press to equip.
    delete field.dataset.choice;
    field.dataset.navGrid = 'true';
    field.classList.add('character-inventory');
    const pictures: { value: string; svg: CharacterPreview }[] = [];
    for (const button of field.querySelectorAll<HTMLButtonElement>('button')) {
      const value = button.dataset.choiceValue!;
      button.setAttribute('aria-label', title(value));
      button.classList.add('character-item');
      const picture = characterPreview(characterStyle({ ...style, [row.field]: value }, colorIndex), color, { weapon: 'none', reducedMotion: true });
      picture.classList.remove('character-preview'); picture.classList.add('character-item-picture');
      picture.removeAttribute('role'); picture.removeAttribute('aria-label'); picture.setAttribute('aria-hidden', 'true');
      const label = element('span', 'character-item-name', title(value));
      const hint = element('span', 'character-item-hint', OPTION_HINTS[value]);
      hint.id = `${button.id}-description`; button.setAttribute('aria-describedby', hint.id);
      const check = element('span', 'character-item-equipped', '✓ Equipped'); check.setAttribute('aria-hidden', 'true');
      button.replaceChildren(picture, label, hint, check);
      pictures.push({ value, svg: picture });
    }
    thumbnails.set(row.field, pictures);
    const note = element('p', 'character-selection-note'); note.setAttribute('aria-live', 'polite');
    selectionNotes.set(row.field, note);
    panel.append(field, note); panels.append(panel); panelMap.set(row.field, panel);
    categories.querySelector(`#character-category-${row.field}`)?.setAttribute('aria-controls', panel.id);
  }

  const tryPanel = element('section', 'character-panel character-try-panel'); tryPanel.id = 'character-panel-preview';
  const tryHeading = element('h3', 'character-panel-title', 'Try it out'); tryHeading.id = 'character-try-heading';
  tryPanel.setAttribute('aria-labelledby', tryHeading.id);
  tryPanel.append(tryHeading, element('p', 'character-panel-hint', 'Put your look through its paces. These controls are just for the preview.'));
  const tryControls = element('div', 'character-try-controls');
  tryControls.append(choiceControl({ id: 'character-pose', label: 'Move', value: pose,
    choices: ['idle', 'run', 'jump', 'duck', 'attack', 'guard', 'victory'].map((value) => ({ value, label: title(value) })),
    onChange: (value) => { pose = value as PreviewPose; refresh(); },
  }));
  tryControls.append(choiceControl({ id: 'character-weapon', label: 'Hold', value: weapon,
    choices: [['none', 'Fists'], ['pistol', 'Pistol'], ['military-shotgun', 'Shotgun'], ['rpg', 'Launcher'], ['sword', 'Sword'], ['return-disc', 'Return disc']].map(([value, label]) => ({ value: value!, label: label! })),
    onChange: (value) => { weapon = value; refresh(); },
  }));
  const directions = element('div', 'character-directions');
  directions.append(choiceControl({ id: 'character-facing', label: 'Face', value: 'right', choices: [{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }],
    onChange: (value) => { facing = value === 'left' ? -1 : 1; refresh(); },
  }), choiceControl({ id: 'character-aim', label: 'Aim', value: 'level', choices: [{ value: 'low', label: 'Low' }, { value: 'level', label: 'Level' }, { value: 'high', label: 'High' }],
    onChange: (value) => { aim = value === 'low' ? -1.1 : value === 'high' ? 1.1 : 0.08; refresh(); },
  }));
  tryControls.append(directions); tryPanel.append(tryControls);
  panels.append(tryPanel); panelMap.set('preview', tryPanel);
  categories.querySelector('#character-category-preview')?.setAttribute('aria-controls', tryPanel.id);
  wardrobe.append(categories, panels);
  layout.append(stage, wardrobe);

  const actions = element('footer', 'character-actions');
  const save = element('div', 'character-save');
  save.append(element('strong', '', '✓ Looks save automatically'), element('span', '', 'Ready for your next brawl.'));
  const navHint = element('p', 'character-nav-hint'); navHint.dataset.inputHint = 'navigation';
  const back = element('button', 'menu-btn primary', 'Done'); back.type = 'button'; back.id = 'character-done'; back.addEventListener('click', onBack);
  actions.append(save, navHint, back);
  card.append(header, layout, actions);

  function refresh() {
    figure.update(style, { weapon, pose, facing, aim, playing });
    playback.textContent = playing ? 'Pause preview' : 'Play preview';
    stageTag.textContent = category === 'preview' ? `${title(pose)} · ${weapon === 'none' ? 'Fists' : weapon === 'military-shotgun' ? 'Shotgun' : weapon === 'rpg' ? 'Launcher' : weapon === 'return-disc' ? 'Return disc' : title(weapon)}` : 'Your fighter';
    personality.textContent = title(style.personality);
    description.textContent = PERSONALITY_HINTS[style.personality];
    equipped.textContent = [title(style.skin ?? 'classic'), style.accessory === 'none' ? 'No accessory' : title(style.accessory), style.marking === 'plain' ? 'Plain' : title(style.marking)].join(' · ');
    for (const [key, panel] of panelMap) panel.hidden = key !== category;
    for (const row of CATEGORIES) {
      const selected = style[row.field] ?? 'classic';
      selectionNotes.get(row.field)!.textContent = row.field === 'personality' ? PERSONALITY_HINTS[style.personality] : `${title(selected)} equipped. ${OPTION_HINTS[selected]}.`;
      if (row.field !== category) continue;
      for (const { value, svg } of thumbnails.get(row.field)!) {
        svg.update(characterStyle({ ...style, [row.field]: value }, colorIndex), { weapon: 'none', pose: 'idle' });
        svg.removeAttribute('aria-label');
        // Inspect facewear and chest details at a useful scale instead of repeating tiny fighters.
        if (row.field === 'accessory') svg.setAttribute('viewBox', value === 'headband' || value === 'glasses' ? '-.57 -.99 1.25 1.15' : '-.9 -.9 1.8 1.9');
        if (row.field === 'marking' || row.field === 'skin') svg.setAttribute('viewBox', '-.58 -.4 1.25 1.1');
      }
    }
  }
  refresh();
}
